import React, { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { 
  ShieldCheck, 
  Lock, 
  Eye, 
  FileText, 
  UserCheck, 
  Globe, 
  Bell, 
  Mail,
  Scale,
  Database
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const PrivacyPolicyPage = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Integrations") return { ...link, route: "/integrations" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Changelog") return { ...link, route: "/changelog" };
        if (link.label === "Security") return { ...link, route: "/security" };
        if (link.label === "Blog") return { ...link, route: "/blog" };
        if (link.label === "Help Center") return { ...link, route: "/help" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "FAQ") return { ...link, route: "/faq" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white text-left">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        {/* Hero Section */}
        <section className="curved-section shell-sky section-animate relative min-h-[40vh] flex items-center overflow-hidden pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="reveal">
              <span className="section-eyebrow">Legal Center</span>
              <h1 className="text-navy mt-6 mb-8">
                Privacy <span className="text-blue-600">Policy.</span>
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Last Updated: February 13, 2026. We are committed to protecting your personal data and your right to privacy.
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="shell-white py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16 reveal">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                    <Database className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">1. Information We Collect</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We collect personal information that you provide to us such as name, address, contact information, passwords and security data, and payment information.
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li><strong>Account Data:</strong> Name, email address, and encrypted password.</li>
                  <li><strong>Project Data:</strong> Task titles, descriptions, and attachments uploaded to PMS.</li>
                  <li><strong>Credentials:</strong> API keys and secrets stored in our encrypted vault.</li>
                  <li><strong>Usage Data:</strong> Log data, IP addresses, and browser metadata.</li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
                    <Scale className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">2. How We Use Your Information</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We process your information for purposes based on legitimate business interests, the fulfillment of our contract with you, compliance with our legal obligations, and/or your consent.
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>To facilitate account creation and logon process.</li>
                  <li>To provide and manage the PMS services.</li>
                  <li>To protect our services from fraud and security threats.</li>
                  <li>To improve our AI extraction models (anonymized data only).</li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
                    <Lock className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">3. Data Security & Storage</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">
                  We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process.
                </p>
                <div className="p-8 bg-gray-50 rounded-3xl border border-gray-100">
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div className="flex gap-4">
                      <ShieldCheck className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-navy">AES-256 Encryption</p>
                        <p className="text-sm text-gray-500">Industry-standard encryption for project data.</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <UserCheck className="w-6 h-6 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-navy">Access Controls</p>
                        <p className="text-sm text-gray-500">Strict role-based permissions.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                    <Globe className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">4. Your Privacy Rights</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  In some regions (like the EEA and UK), you have certain rights under applicable data protection laws. These may include the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>Request access and obtain a copy of your personal information.</li>
                  <li>Request rectification or erasure of your data.</li>
                  <li>Restrict the processing of your personal information.</li>
                  <li>Data portability and right to object.</li>
                </ul>
              </div>

              <div className="p-10 bg-blue-50 rounded-[40px] text-center">
                <h3 className="text-2xl font-bold text-navy mb-4">Contact Our Privacy Officer</h3>
                <p className="text-gray-600 mb-8">If you have questions or comments about this policy, you may email us at:</p>
                <a href="mailto:privacy@pms.example.io" className="text-xl font-black text-blue-600 hover:underline">
                  privacy@pms.example.io
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default PrivacyPolicyPage;
